import { ThemeProvider } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license'
import { impersonationSelector } from 'admin/services/selectors'
import { ComposerMediaContextProvider } from 'components/ComposerMediaContext/ComposerMediaContextProvider'
import { FacebookSDK } from 'components/FacebookSDK'
import IntercomProvider from 'components/IntercomProvider'
import { MaintenanceView } from 'components/MaintenanceView'
import TwitterSDK from 'components/TwitterSDK'
import { MAINTENANCE_ON, MUI_X_LICENSE_KEY } from 'config'
import { Fragment, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useDispatch, useSelector } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { Root } from 'routes'
import { checkTokens, logout } from 'services/auth'
import { productsSelector } from 'services/product'
import { HS_EVENT_APP_BOOT, trackHubspotEvent } from 'services/tracking/hubspot'
import { currentUserSelector } from 'services/users/selectors'
import { StoreThunkDispatch } from 'store/state'
import { theme } from 'theme/theme-md'
import { initializeGoogleAnalytics } from 'tracking/google-analytics'
import { ErrorFallback } from './components/ErrorFallback'
import { RouteErrorView } from './components/ErrorFallback/RouteErrorView'
import PPIntlProvider from './components/PPIntlProvider'

// EXPL: Bypass type checking, because of error in @types/react
const ErrorHandler = ErrorBoundary as any

// EXPL: Create the router once, outside the component. Creating it on every App render gives
// every useNavigate() a new identity, which re-runs effects depending on navigate (e.g. /logout).
const router = createBrowserRouter([{
  path: '/*',
  element: <Root />,
  errorElement: <RouteErrorView />
}])

export function App() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const onReset = () => dispatch(logout())
  const hasUser = Boolean(useSelector(currentUserSelector))
  const products = useSelector(productsSelector)
  const [loading, setLoading] = useState(products.length === 0 && hasUser)
  const impersonatedUserId = useSelector(impersonationSelector)?.userId

  // EXPL: Re-runs when impersonation starts or stops, so the products and add-ons are refreshed for the new identity
  useEffect(() => {
    let sub: any
    if (hasUser) {
      sub = dispatch(checkTokens()).subscribe(() => {
        setLoading(false)
      }, (e: any) => {
        console.log('error fetching products', e)
        setLoading(false)
      })
    }
    return () => {
      sub?.unsubscribe()
    }
  }, [dispatch, hasUser, impersonatedUserId])

  useEffect(() => {
    if (__PROD__) {
      initializeGoogleAnalytics()
    }
    LicenseInfo.setLicenseKey(MUI_X_LICENSE_KEY)
  }, [])

  useEffect(() => {
    if (hasUser) {
      dispatch(trackHubspotEvent(HS_EVENT_APP_BOOT, { type: 'web' }))
    }
  }, [dispatch, hasUser])

  if (MAINTENANCE_ON) {
    return (
      <MaintenanceView
        key="maintenance-view"
        title="Maintenance break"
        subtitle="We'll be back online in an hour."
      />
    )
  }

  if (loading) {
    return null
  }

  return (
    <PPIntlProvider>
      <ThemeProvider theme={theme}>
        <IntercomProvider>
          <Fragment>
            <FacebookSDK />
            <TwitterSDK />
            <ErrorHandler
              FallbackComponent={ErrorFallback}
              onReset={onReset}
            >
              <ComposerMediaContextProvider>
                <RouterProvider router={router} />
              </ComposerMediaContextProvider>
            </ErrorHandler>
          </Fragment>
        </IntercomProvider>
      </ThemeProvider>
    </PPIntlProvider>
  )
}

export default App
